import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Box, Button, Grid, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import theme from "../../../theme";
import { getPendingRegistrations, approveRegistration, rejectRegistration } from "../../../services/core-api";
import { useSnackbar } from "../../../components/common/SnackbarContext";

interface PendingUser {
  id: number;
  username: string;
  name: string;
  surname: string;
  company: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  zone?: string;
  group?: string;
  ust_id?: string;
  plz?: string;
}

const PendingRegistrationsPage = () => {
  const [users, setUsers] = React.useState<PendingUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState<number | null>(null);
  const { showSnackbar } = useSnackbar();

  const fetchPending = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPendingRegistrations();
      setUsers(res.users || []);
    } catch {
      setUsers([]);
      showSnackbar("Ausstehende Registrierungen konnten nicht geladen werden.", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  React.useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleApprove = async (userId: number) => {
    try {
      setActionLoading(userId);
      await approveRegistration(userId);
      showSnackbar("Benutzer wurde genehmigt.", "success");
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Genehmigung fehlgeschlagen.";
      showSnackbar(typeof msg === "string" ? msg : JSON.stringify(msg), "error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId: number) => {
    try {
      setActionLoading(userId);
      await rejectRegistration(userId);
      showSnackbar("Registrierung wurde abgelehnt.", "success");
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (err: any) {
      const msg = err?.response?.data?.detail || "Ablehnung fehlgeschlagen.";
      showSnackbar(typeof msg === "string" ? msg : JSON.stringify(msg), "error");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "18px",
              fontFamily: "Inter",
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            Registrierungsanfragen
          </Typography>
        </Grid>
      </Grid>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
        <TableContainer
          sx={{ maxHeight: 640, border: "1px solid #f1f1f1", p: 2 }}
        >
          <Table stickyHeader aria-label="Ausstehende Registrierungen">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>Benutzername</TableCell>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>Vorname</TableCell>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>Nachname</TableCell>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>Firma</TableCell>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>E-Mail</TableCell>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>Land</TableCell>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>Ort</TableCell>
                <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, fontFamily: "Inter" }}>
                    Wird geladen …
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, fontFamily: "Inter" }}>
                    Keine ausstehenden Registrierungsanfragen.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell sx={{ fontFamily: "Inter" }}>{row.username}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter" }}>{row.name}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter" }}>{row.surname}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter" }}>{row.company}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter" }}>{row.email}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter" }}>{row.country || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "Inter" }}>{row.city || "—"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleApprove(row.id)}
                          disabled={actionLoading === row.id}
                          sx={{
                            fontFamily: "Inter",
                            textTransform: "capitalize",
                            bgcolor: "#2e7d32",
                            "&:hover": { bgcolor: "#1b5e20" },
                          }}
                        >
                          Genehmigen
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleReject(row.id)}
                          disabled={actionLoading === row.id}
                          sx={{ fontFamily: "Inter", textTransform: "capitalize" }}
                        >
                          Ablehnen
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default PendingRegistrationsPage;
